import React from 'react';
import { costCenters } from '../../data/costAccountingData';

const CostCentersView = () => {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">成本中心</h2>
        <p className="text-gray-600">Cost Centers</p>
      </div>

      {/* Cost Centers header image */}
      <div className="relative h-48 mb-6 overflow-hidden rounded-lg shadow-md">
        <img 
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
          alt="Cost Centers" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <h1 className="text-white text-3xl font-bold">成本中心管理</h1>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">代碼</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名稱</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">類型</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">負責人</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">預算</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costCenters.map((center) => (
                <tr key={center.cost_center_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{center.cost_center_code}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{center.cost_center_name}</div>
                    <div className="text-sm text-gray-500">{center.cost_center_name_en}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      center.cost_center_type === 'PRODUCTION' ? 'bg-blue-100 text-blue-800' : 
                      center.cost_center_type === 'SERVICE' ? 'bg-green-100 text-green-800' : 
                      'bg-yellow-100 text-yellow-800'}`}>
                      {center.cost_center_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{center.manager}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${center.budget_amount.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${center.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {center.is_active ? '活躍' : '非活躍'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Budget Summary */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">預算摘要</h3>
          <div className="space-y-4">
            {['PRODUCTION', 'SERVICE', 'ADMINISTRATIVE'].map(type => {
              const centers = costCenters.filter(center => center.cost_center_type === type);
              const totalBudget = centers.reduce((sum, center) => sum + center.budget_amount, 0);
              const count = centers.length;
              return (
                <div key={type} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                  <div>
                    <p className="font-medium text-gray-700">{type}</p>
                    <p className="text-sm text-gray-500">{count} 中心</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">${totalBudget.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">年度預算</p>
                  </div>
                </div>
              );
            })}
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-md">
              <div>
                <p className="font-medium text-blue-700">總計</p>
                <p className="text-sm text-blue-500">{costCenters.length} 中心</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-blue-900">
                  ${costCenters.reduce((sum, center) => sum + center.budget_amount, 0).toLocaleString()}
                </p>
                <p className="text-xs text-blue-500">年度總預算</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Center Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">成本中心分佈</h3>
          <div className="space-y-4">
            {['PRODUCTION', 'SERVICE', 'ADMINISTRATIVE'].map(type => {
              const count = costCenters.filter(center => center.cost_center_type === type).length;
              const percentage = (count / costCenters.length) * 100;
              return (
                <div key={type} className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${
                        type === 'PRODUCTION' ? 'bg-blue-100 text-blue-800' : 
                        type === 'SERVICE' ? 'bg-green-100 text-green-800' : 
                        'bg-yellow-100 text-yellow-800'}`}>
                        {type}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-gray-800">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-gray-200">
                    <div style={{ width: `${percentage}%` }} 
                         className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                           type === 'PRODUCTION' ? 'bg-blue-500' : 
                           type === 'SERVICE' ? 'bg-green-500' : 
                           'bg-yellow-500'}`}>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">{count} 中心</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active vs Inactive */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">活躍狀態</h3>
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span className="text-sm text-gray-700">活躍</span>
            </div>
            <div className="inline-flex items-center ml-6">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span className="text-sm text-gray-700">非活躍</span>
            </div>
          </div>
          <div className="relative pt-1">
            <div className="overflow-hidden h-4 mb-4 text-xs flex rounded">
              {(() => {
                const activeCount = costCenters.filter(center => center.is_active).length;
                const inactiveCount = costCenters.length - activeCount;
                const activePercentage = (activeCount / costCenters.length) * 100;
                const inactivePercentage = 100 - activePercentage;
                return (
                  <>
                    <div style={{ width: `${activePercentage}%` }} 
                         className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500">
                      {activePercentage > 10 && `${activeCount}`}
                    </div>
                    <div style={{ width: `${inactivePercentage}%` }} 
                         className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-red-500">
                      {inactivePercentage > 10 && `${inactiveCount}`}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="text-center text-sm text-gray-500">
            {costCenters.filter(center => center.is_active).length} 活躍 / {costCenters.length} 總計
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostCentersView;
